/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/options
 * @desc Column Definition for metadata options
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { getFieldsOptions } from 'labstep-web/components/Metadata/Form/fields';
import { ICONS } from 'labstep-web/constants/icons';
import {
  suppressKeyboardEventClearCells,
  suppressKeyboardEventDisableEnterOnEdit,
} from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import { FormShowEditSelectOptions } from 'labstep-web/core/Form/ShowEdit/SelectOptions';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import rules from 'labstep-web/services/validation/rules';
import { Label } from 'semantic-ui-react';
import styles from './styles.module.scss';

export const getMetadataOptionsColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.secondary,
      cornerIcon:
        ICONS[parentEntityClass as keyof typeof ICONS].primary,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
        field: 'options',
        entityName: Metadata.entityName,
        fieldType: PostFilterFieldTypes.metadata,
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    suppressKeyboardEvent: (params: any): boolean =>
      suppressKeyboardEventDisableEnterOnEdit(params) ||
      suppressKeyboardEventClearCells(params),
    valueClearer: () => null,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }
      return JSON.stringify(metadata.options);
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (params.newValue == null) {
        if (!metadata) {
          return false;
        }
        setValue(
          'options',
          null,
          {
            entityName: metadata.entityName,
            id: metadata.idAttr,
          },
          rules.metadata.options,
          GridColumnDefinitionService.getOptions(params),
        );
        return true;
      }
      let newValue = params.newValue;
      if (typeof newValue === 'string') {
        try {
          newValue = JSON.parse(newValue);
        } catch {
          return false;
        }
      }

      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass].metadata_thread.idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }
        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };

        createOrUpdateValue({
          entityName: Metadata.entityName,
          body: {
            type,
            label: metadataLabel,
            metadata_thread_id: metadataThreadId,
            options: newValue,
          },
          createProps: props,
          options: GridColumnDefinitionService.getOptions(params),
        });
        return true;
      }
      setValue(
        'options',
        newValue,
        {
          entityName: metadata.entityName,
          id: metadata.idAttr,
        },
        rules.metadata.options,
        GridColumnDefinitionService.getOptions(params),
      );
      return true;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      return (
        <>
          {metadata?.optionsSelectedValues.length ? (
            metadata.optionsSelectedValues.map((value: string) => (
              <Label key={value} circular>
                {value}
              </Label>
            ))
          ) : (
            <DataGridPlaceholder
              children="Select"
              params={params}
              editable
            />
          )}
        </>
      );
    },
    cellEditorPopup: true,
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        // Metadata doesn't exist - need to create it
        const metadataThreadParent =
          parentEntityClass !== params.node.data.entityName
            ? params.node.data[parentEntityClass]
            : params.node.data;
        if (!metadataThreadParent) {
          stopEditingAndFocus(params);
          return '';
        }

        const metadataTemplate = metadataThreadParent.template
          ? metadataThreadParent.template.metadata_thread.metadatas.find(
              (m: Metadata) =>
                m.type === type && m.label === metadataLabel,
            )
          : null;

        return (
          <div
            className={styles.container}
            style={{
              width: params.column.getActualWidth(),
            }}
          >
            <FormShowEditSelectOptions
              canEdit={true}
              children={null}
              initialEditingState
              onToggle={(): void => stopEditingAndFocus(params)}
              options={GridColumnDefinitionService.getOptions(params)}
              field={
                getFieldsOptions(
                  metadataTemplate,
                  true,
                  false,
                  true,
                )[0]
              }
              entity={metadataTemplate}
              entityName={Metadata.entityName}
              createProps={{
                parentName: MetadataThread.entityName,
                parentId: metadataThreadParent.metadata_thread.idAttr,
                createBody: {
                  type,
                  label: metadataLabel,
                  metadata_thread_id:
                    metadataThreadParent.metadata_thread.guid,
                },
              }}
              onSubmit={(
                submittedValue: any,
                callback: () => void,
              ) => {
                const metadataThreadId =
                  metadataThreadParent.metadata_thread.idAttr;
                const props = {
                  parentName: MetadataThread.entityName,
                  parentId: metadataThreadId,
                };

                createOrUpdateValue({
                  entityName: Metadata.entityName,
                  body: {
                    type,
                    label: metadataLabel,
                    metadata_thread_id: metadataThreadId,
                    options: submittedValue,
                  },
                  createProps: props,
                  options:
                    GridColumnDefinitionService.getOptions(params),
                });
                // Don't call callback as we want to close the editor
                stopEditingAndFocus(params);
              }}
            />
          </div>
        );
      }

      // Metadata exists but might be being created (temporary negative ID)
      if (String(metadata.id)[0] === '-') {
        stopEditingAndFocus(params);
        return '';
      }

      // Metadata exists - use normal edit mode
      return (
        <div
          className={styles.container}
          style={{
            width: params.column.getActualWidth(),
          }}
        >
          <FormShowEditSelectOptions
            canEdit={true}
            children={null}
            initialEditingState
            onToggle={(): void => stopEditingAndFocus(params)}
            options={GridColumnDefinitionService.getOptions(params)}
            field={
              getFieldsOptions(
                metadata,
                true,
                metadata.is_template,
                true,
              )[0]
            }
            entity={metadata}
            entityName={Metadata.entityName}
          />
        </div>
      );
    },
  };
};
